import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { t, translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import SearchBar from '../components/shared/SearchBar.jsx';
import { useAuth } from '../hooks/useAuth.js';
import SubscriptionUpgradeModal from '../components/SubscriptionUpgradeModal.jsx';
import HandoverModal from '../components/shared/HandoverModal.jsx';
import EraseMemberModal from '../components/shared/EraseMemberModal.jsx';
import { hasModule } from '../utils/plan.js';
import { parseServerDate } from '../utils/dates.js';
import { isStandalone, openFile } from '../utils/openFile.js';
import { loadDepartments } from '../utils/departments.js';
import MemberRoles from '../components/shared/MemberRoles.jsx';

function Avatar({ name, size = 32 }) {
  return (
    <div style={`flex-shrink:0;width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:${Math.round(size * 0.4)}px;font-weight:700;color:#fff;background:var(--color-primary)`}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

function RoleBadges({ roles = [], max = 2 }) {
  const shown = roles.slice(0, max);
  const rest = roles.length - max;
  return (
    <div style="display:flex;flex-wrap:wrap;gap:4px;align-items:center">
      {shown.map(r => (
        <span key={r.id} style="font-size:11px;background:var(--color-bg-secondary,#f3f4f6);color:var(--color-text-muted);padding:2px 8px;border-radius:10px;border:1px solid var(--color-border);white-space:nowrap">
          {r.name}
        </span>
      ))}
      {rest > 0 && <span style="font-size:11px;color:var(--color-text-muted)">+{rest}</span>}
    </div>
  );
}

function SectionBadges({ sections = [], max = 2 }) {
  const shown = sections.slice(0, max);
  const rest = sections.length - max;
  return (
    <div style="display:flex;flex-wrap:wrap;gap:4px;align-items:center">
      {shown.map(s => (
        <span key={s} style="font-size:11px;background:var(--color-primary);color:#fff;padding:2px 8px;border-radius:10px;white-space:nowrap">
          {s}
        </span>
      ))}
      {rest > 0 && <span style="font-size:11px;color:var(--color-text-muted)">+{rest}</span>}
    </div>
  );
}

function SectionPicker({ selected, onChange, disabled, sectionGroups }) {
  const toggle = (categoryName) => {
    if (disabled) return;
    onChange(selected.includes(categoryName)
      ? selected.filter(s => s !== categoryName)
      : [...selected, categoryName]);
  };

  if (!sectionGroups?.length) return <p class="text-sm text-muted">Geen secties beschikbaar.</p>;

  const CategoryButton = ({ cat }) => {
    const active = selected.includes(cat.name);
    return (
      <button
        key={cat.id}
        type="button"
        disabled={disabled}
        onClick={() => toggle(cat.name)}
        style={`padding:3px 10px;border-radius:12px;font-size:12px;cursor:${disabled ? 'default' : 'pointer'};border:1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'};background:${active ? 'var(--color-primary)' : 'transparent'};color:${active ? '#fff' : 'var(--color-text-muted)'};transition:all .15s`}
      >
        {cat.name}
      </button>
    );
  };

  return (
    <div>
      {sectionGroups.map((section) => (
        <div key={section.id} style="margin-bottom:16px">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted);margin-bottom:8px;border-bottom:1px solid var(--color-border);padding-bottom:4px">
            {section.name}
          </div>

          {/* Direct categories (sections without commissions, e.g. Dagelijks Bestuur) */}
          {(section.categories || []).length > 0 && (
            <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">
              {section.categories.map(cat => <CategoryButton cat={cat} />)}
            </div>
          )}

          {/* Commissions with their sub-categories */}
          {(section.commissions || []).map(commission => (
            <div key={commission.id} style="margin-bottom:10px;padding-left:8px;border-left:2px solid var(--color-border)">
              <div style="font-size:11px;font-weight:600;color:var(--color-text-secondary,var(--color-text-muted));margin-bottom:5px">
                {commission.name}
              </div>
              <div style="display:flex;flex-wrap:wrap;gap:6px">
                {(commission.categories || []).map(cat => <CategoryButton cat={cat} />)}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function UsersView() {
  const { hasPermission, userId: myId } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sectionGroups, setSectionGroups] = useState([]);
  const [search, setSearch] = useState('');

  // Detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [pendingSections, setPendingSections] = useState([]);
  const [savingSections, setSavingSections] = useState(false);
  const [sectionsSaved, setSectionsSaved] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Edit profile modal
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({ display_name: '', email: '' });
  const [editProfileSaving, setEditProfileSaving] = useState(false);
  const [editProfileError, setEditProfileError] = useState('');

  // Handover (overdracht), also the first step of removing someone
  const [handoverMode, setHandoverMode] = useState(null); // 'handover' | 'deactivate'
  const [handoverHistory, setHandoverHistory] = useState([]);

  // Erasing on request (AVG), also for people removed earlier
  const [eraseFor, setEraseFor] = useState(null);
  const [formerMembers, setFormerMembers] = useState([]);
  const [formerOpen, setFormerOpen] = useState(false);

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', roleId: '' });
  const [inviteError, setInviteError] = useState('');
  const [inviteTempPassword, setInviteTempPassword] = useState('');
  const [upgradeModal, setUpgradeModal] = useState(null);

  // CSV import modal
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  function downloadTemplate() {
    if (isStandalone()) return openFile('/api/users/import-template');
    window.location.href = '/api/users/import-template';
  }

  async function handleImportSubmit(e) {
    e.preventDefault();
    if (!importFile) return;
    setImportLoading(true);
    setImportError('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await fetch('/api/users/import-csv', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'server_error');
      setImportResult(data);
      fetchUsers();
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImportLoading(false);
    }
  }

  function closeImport() {
    setImportOpen(false);
    setImportFile(null);
    setImportResult(null);
    setImportError('');
  }

  const canUpdate = hasPermission('users', 'update');
  const canDelete = hasPermission('users', 'delete');

  useEffect(() => {
    api('/api/users').then(setUsers).catch(() => {});
    api('/api/users/roles/list').then(setRoles).catch(() => {});
    loadDepartments();
    api('/api/settings/sections').then(setSectionGroups).catch(() => {});
    if (canDelete) api('/api/users/former').then(setFormerMembers).catch(() => {});
  }, []);

  function refreshUsers(keepSelected = false) {
    api('/api/users').then(data => {
      setUsers(data);
      if (keepSelected && selectedUser) {
        const fresh = data.find(u => u.id === selectedUser.id);
        if (fresh) {
          setSelectedUser(fresh);
          setPendingSections(fresh.sections || []);
        }
      }
    }).catch(() => {});
  }

  function openDetail(user) {
    setSelectedUser(user);
    setPendingSections(user.sections || []);
    setDetailError('');
    setSectionsSaved(false);
    setDetailOpen(true);
    setHandoverHistory([]);
    if ((canUpdate || canDelete) && hasModule('handovers')) api(`/api/handovers/user/${user.id}`).then(setHandoverHistory).catch(() => {});
  }

  async function handleSaveSections(userId) {
    setSavingSections(true);
    setSectionsSaved(false);
    try {
      await api(`/api/users/${userId}/sections`, {
        method: 'PUT',
        body: JSON.stringify({ sections: pendingSections }),
      });
      setSectionsSaved(true);
      refreshUsers(true);
      setTimeout(() => setSectionsSaved(false), 3000);
    } catch (err) {
      setDetailError(translateError(err.code));
    } finally {
      setSavingSections(false);
    }
  }

  function openEditProfile(user) {
    setEditProfileForm({ display_name: user.display_name || '', email: user.email || '' });
    setEditProfileError('');
    setEditProfileOpen(true);
  }

  async function handleSaveProfile() {
    setEditProfileSaving(true);
    setEditProfileError('');
    try {
      const updated = await api(`/api/users/${selectedUser.id}/profile`, {
        method: 'PUT',
        body: JSON.stringify(editProfileForm),
      });
      setEditProfileOpen(false);
      setSelectedUser(prev => ({ ...prev, display_name: updated.display_name, email: updated.email }));
      refreshUsers(true);
    } catch (err) {
      setEditProfileError({
        member_of_other_club: 'Deze persoon is ook lid van een andere club. Vraag diegene het e-mailadres zelf te laten wijzigen, of vraag de platformbeheerder.',
        insufficient_permissions: 'Het e-mailadres van deze persoon kun jij niet wijzigen (bijvoorbeeld een clubbeheerder). Vraag een clubbeheerder.',
        invalid_email: 'Vul een geldig e-mailadres in.',
      }[err.code] || translateError(err.code));
    } finally {
      setEditProfileSaving(false);
    }
  }

  // Twee-staps-inloggen off for someone who lost their phone (they set it up again)
  const [mfaResetFor, setMfaResetFor] = useState(null);
  function handleMfaReset() { setMfaResetFor(selectedUser); }
  async function confirmMfaReset() {
    try {
      await api(`/api/users/${mfaResetFor.id}/mfa-reset`, { method: 'POST', body: '{}' });
      refreshUsers(true);
    } catch (err) {
      setDetailError(err.code === 'member_of_other_club'
        ? 'Deze persoon is ook lid van een andere club. Vraag de platformbeheerder om twee-staps-inloggen uit te zetten.'
        : translateError(err.code));
    }
    setMfaResetFor(null);
  }

  function handleHandoverDone(result) {
    if (handoverMode === 'deactivate') {
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
      setDetailOpen(false);
      setSelectedUser(null);
      api('/api/users/former').then(setFormerMembers).catch(() => {});
    } else {
      refreshUsers(true);
      api(`/api/handovers/user/${selectedUser.id}`).then(setHandoverHistory).catch(() => {});
      if (result?.id) route(`/overdracht/${result.id}`);
    }
    setHandoverMode(null);
  }

  function handleErased(user) {
    setUsers(prev => prev.filter(u => u.id !== user.id));
    setFormerMembers(prev => prev.filter(u => u.id !== user.id));
    if (selectedUser?.id === user.id) { setDetailOpen(false); setSelectedUser(null); }
    setEraseFor(null);
  }

  function handleInviteSubmit(e) {
    e.preventDefault();
    setInviteError('');
    api('/api/users/invite', { method: 'POST', body: JSON.stringify(inviteForm) })
      .then(data => {
        setInviteTempPassword(data.tempPassword || '');
        setInviteForm({ name: '', email: '', roleId: '' });
        refreshUsers();
      })
      .catch(err => {
        if (err.code === 'subscription_limit_reached' && err.nextTier && hasPermission('settings', 'update')) {
          setInviteOpen(false);
          setUpgradeModal({ currentTierLabel: err.tierLabel, nextTier: err.nextTier });
        } else if (err.code === 'subscription_limit_reached') {
          setInviteError(`Het maximum aantal gebruikers van ${err.tierLabel || 'je pakket'} is bereikt.${err.nextTier ? ` Vraag je clubbeheerder om over te stappen naar ${err.nextTier.label}.` : ''}`);
        } else {
          setInviteError(err.detail || translateError(err.code));
        }
      });
  }

  function closeInvite() {
    setInviteOpen(false);
    setInviteError('');
    setInviteTempPassword('');
    setInviteForm({ name: '', email: '', roleId: '' });
  }

  const sectionsChanged = selectedUser &&
    JSON.stringify([...pendingSections].sort()) !==
    JSON.stringify([...(selectedUser.sections || [])].sort());

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.display_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{t('user.title')}</h1>
        {hasPermission('users', 'create') && (
          <div style="display:flex;gap:8px">
            <Button variant="secondary" onClick={() => setImportOpen(true)}>Importeren</Button>
            <Button onClick={() => setInviteOpen(true)}>{t('user.invite')}</Button>
          </div>
        )}
      </div>

      <div style="margin-bottom:16px;max-width:360px">
        <SearchBar value={search} onSearch={setSearch} placeholder={t('common.search')} />
      </div>

      <div class="card" style="overflow:hidden">
        {/* Table header */}
        <div style="display:grid;grid-template-columns:2fr 2fr 1fr 80px;gap:12px;padding:10px 16px;background:var(--color-surface-alt,#f9fafb);border-bottom:1px solid var(--color-border);font-size:11px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em">
          <span>Naam</span>
          <span>E-mail</span>
          <span>Rollen</span>
          <span />
        </div>

        {filtered.length === 0 && (
          <div class="text-center text-muted" style="padding:48px">{t('common.noResults')}</div>
        )}

        {filtered.map(user => (
          <div
            key={user.id}
            class="list-item-row"
            style="display:grid;grid-template-columns:2fr 2fr 1fr 80px;gap:12px;padding:10px 16px;border-bottom:1px solid var(--color-border);align-items:center"
          >
            <div style="display:flex;align-items:center;gap:10px;min-width:0">
              <Avatar name={user.display_name || user.email} size={32} />
              <div style="display:flex;flex-direction:column;min-width:0">
                <span style="font-size:14px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                  {user.display_name}
                </span>
                {!!user.is_platform_admin && (
                  <span class="badge badge-default" title="Medewerker van 4U, voor ondersteuning op verzoek van de club" style="align-self:flex-start;margin-top:2px;font-size:10px">4U-ondersteuning</span>
                )}
              </div>
            </div>
            <div style="font-size:13px;color:var(--color-text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              {user.email}
            </div>
            <RoleBadges roles={user.roles} max={2} />
            <div style="display:flex;justify-content:flex-end">
              <Button variant="secondary" size="sm" onClick={() => openDetail(user)}>
                Detail
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Oud-leden: removed earlier, their data stays until erased */}
      {canDelete && formerMembers.length > 0 && (
        <div class="card" style="margin-top:16px;padding:12px 16px">
          <button type="button" onClick={() => setFormerOpen(o => !o)}
            style="background:none;border:none;padding:0;cursor:pointer;font-size:14px;font-weight:600;color:var(--color-text)">
            {formerOpen ? '▾' : '▸'} Oud-leden ({formerMembers.length})
          </button>
          {formerOpen && (
            <div style="margin-top:8px">
              <div class="text-sm text-muted" style="margin-bottom:8px">Eerder verwijderd. Hun werk en gegevens staan nog in de club; vraagt iemand om alles te wissen, dan doe je dat hier.</div>
              {formerMembers.map(u => (
                <div key={u.id} style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:6px 0;border-top:1px solid var(--color-border)">
                  <span style="font-size:14px">{u.display_name}</span>
                  <Button variant="secondary" size="sm" onClick={() => setEraseFor(u)}>Gegevens wissen</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Detail modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={detailOpen}
        title={selectedUser?.display_name || ''}
        onClose={() => setDetailOpen(false)}
      >
        {selectedUser && (
          <div style="display:flex;flex-direction:column;gap:20px">
            {detailError && <div class="alert alert-error">{detailError}</div>}

            {/* Profiel info */}
            <div>
              <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:10px">Profiel</div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 16px;font-size:13px">
                <div>
                  <div style="color:var(--color-text-muted);font-size:11px;margin-bottom:2px">E-mailadres</div>
                  <div>{selectedUser.email}</div>
                </div>
                <div>
                  <div style="color:var(--color-text-muted);font-size:11px;margin-bottom:2px">MFA-status</div>
                  <div>{selectedUser.mfa_enabled ? t('common.active') : t('common.inactive')}</div>
                  {selectedUser.mfa_enabled && canUpdate && (
                    <button type="button" class="text-sm" style="background:none;border:none;padding:0;color:var(--color-primary);cursor:pointer;text-decoration:underline"
                      onClick={handleMfaReset}>Uitzetten (telefoon kwijt)</button>
                  )}
                </div>
                <div>
                  <div style="color:var(--color-text-muted);font-size:11px;margin-bottom:2px">Lid sinds</div>
                  <div>{selectedUser.joined_at ? parseServerDate(selectedUser.joined_at).toLocaleDateString() : '—'}</div>
                </div>
              </div>
              {canUpdate && (
                <div style="margin-top:10px">
                  <Button variant="secondary" size="sm" onClick={() => openEditProfile(selectedUser)}>
                    Bewerken
                  </Button>
                </div>
              )}
            </div>

            <hr style="border:none;border-top:1px solid var(--color-border);margin:0" />

            {/* Rollen */}
            <div>
              <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:10px">Rollen</div>
              <MemberRoles key={selectedUser.id} user={selectedUser} roles={roles} canUpdate={canUpdate}
                onChanged={() => refreshUsers(true)} onError={setDetailError} />
            </div>

            <hr style="border:none;border-top:1px solid var(--color-border);margin:0" />

            {/* Secties */}
            <div>
              <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:10px">Secties</div>
              <SectionPicker
                selected={pendingSections}
                onChange={setPendingSections}
                disabled={!canUpdate}
                sectionGroups={sectionGroups}
              />
              {canUpdate && (
                <div style="margin-top:10px;display:flex;align-items:center;gap:12px">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleSaveSections(selectedUser.id)}
                    disabled={savingSections || !sectionsChanged}
                  >
                    {savingSections ? t('common.loading') : t('user.saveSections')}
                  </Button>
                  {sectionsSaved && (
                    <span style="font-size:13px;color:var(--color-success,#16a34a)">✓ {t('user.sectionsSaved')}</span>
                  )}
                </div>
              )}
            </div>

            {/* Overdracht */}
            {(canUpdate || canDelete) && hasModule('handovers') && (
              <>
                <hr style="border:none;border-top:1px solid var(--color-border);margin:0" />
                <div>
                  <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:10px">Overdracht</div>
                  <div class="text-sm text-muted" style="margin-bottom:8px">Stopt {selectedUser.display_name.split(' ')[0]} met een functie? Draag functies en openstaand werk over aan een opvolger.</div>
                  {handoverHistory.map(hv => (
                    <a key={hv.id} href={`/overdracht/${hv.id}`} style="display:block;font-size:13px;margin-bottom:4px">
                      {parseServerDate(hv.created_at).toLocaleDateString()}: {hv.from_name || 'Oud-lid'} → {hv.to_name || 'Oud-lid'} · {hv.item_count} onderde{hv.item_count === 1 ? 'el' : 'len'} · {hv.acknowledged}/{hv.receivers} bevestigd
                    </a>
                  ))}
                  <Button variant="secondary" size="sm" onClick={() => setHandoverMode('handover')}>Overdracht starten</Button>
                </div>
              </>
            )}

            {/* Footer actions */}
            {canDelete && (
              <>
                <hr style="border:none;border-top:1px solid var(--color-border);margin:0" />
                <div class="flex gap-2 justify-end">
                  {selectedUser.id !== myId && (
                    <Button variant="secondary" size="sm" onClick={() => setEraseFor(selectedUser)}>
                      Gegevens wissen
                    </Button>
                  )}
                  <Button variant="danger" size="sm" onClick={() => setHandoverMode('deactivate')}>
                    {t('common.delete')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Edit profile modal ────────────────────────────────────────────── */}
      <Modal
        isOpen={editProfileOpen}
        title="Gebruiker bewerken"
        onClose={() => !editProfileSaving && setEditProfileOpen(false)}
      >
        <div class="form-group">
          <label class="form-label">Naam</label>
          <input
            class="form-input"
            type="text"
            value={editProfileForm.display_name}
            onInput={e => setEditProfileForm(f => ({ ...f, display_name: e.target.value }))}
          />
        </div>
        <div class="form-group">
          <label class="form-label">E-mailadres</label>
          <input
            class="form-input"
            type="email"
            value={editProfileForm.email}
            onInput={e => setEditProfileForm(f => ({ ...f, email: e.target.value }))}
          />
        </div>
        {editProfileError && <div class="alert alert-error mb-3">{editProfileError}</div>}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setEditProfileOpen(false)} disabled={editProfileSaving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveProfile}
            disabled={editProfileSaving || !editProfileForm.display_name.trim() || !editProfileForm.email.trim()}
          >
            {editProfileSaving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={!!mfaResetFor} title="Twee-staps-inloggen uitzetten" onClose={() => setMfaResetFor(null)}>
        <p style="font-size:14px;margin:0 0 12px">Zet twee-staps-inloggen uit voor <strong>{mfaResetFor?.display_name}</strong>, bijvoorbeeld omdat de telefoon kwijt is. Diegene logt daarna alleen met het wachtwoord in en krijgt een melding om het opnieuw aan te zetten. Dit komt in het auditlog.</p>
        <p class="text-sm text-muted" style="margin:0 0 16px">Doe dit alleen als je zeker weet dat het verzoek echt van deze persoon komt.</p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setMfaResetFor(null)}>Annuleren</Button>
          <Button variant="danger" onClick={confirmMfaReset}>Uitzetten</Button>
        </div>
      </Modal>

      {/* ── Handover / delete modal ──────────────────────────────────────── */}
      <HandoverModal
        isOpen={!!handoverMode}
        user={selectedUser}
        mode={handoverMode || 'handover'}
        onClose={() => setHandoverMode(null)}
        onDone={handleHandoverDone}
      />

      <EraseMemberModal user={eraseFor} onClose={() => setEraseFor(null)} onDone={handleErased} />

      {/* ── Invite modal ──────────────────────────────────────────────────── */}
      <Modal isOpen={inviteOpen} title={t('user.inviteUser')} onClose={closeInvite}>
        {inviteTempPassword ? (
          <div>
            <div class="alert alert-success mb-4">
              <strong>{t('user.inviteSuccess')}</strong>
              <div class="mt-2">{t('user.tempPassword')}: <code style="background:var(--color-bg-secondary);padding:2px 6px;border-radius:4px">{inviteTempPassword}</code></div>
              <div class="mt-1 text-sm text-muted">{t('user.tempPasswordNote')}</div>
            </div>
            <Button onClick={closeInvite}>{t('common.close')}</Button>
          </div>
        ) : (
          <form onSubmit={handleInviteSubmit}>
            {inviteError && <div class="alert alert-error mb-4">{inviteError}</div>}
            <div class="form-group">
              <label class="form-label">{t('user.name')}</label>
              <input class="form-input" type="text" required value={inviteForm.name} onInput={e => setInviteForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div class="form-group">
              <label class="form-label">{t('user.email')}</label>
              <input class="form-input" type="email" required value={inviteForm.email} onInput={e => setInviteForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div class="form-group">
              <label class="form-label">{t('user.role')}</label>
              <select class="form-select" required value={inviteForm.roleId} onChange={e => setInviteForm(f => ({ ...f, roleId: e.target.value }))}>
                <option value="">{t('user.selectRole')}</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div class="modal-footer">
              <Button type="button" variant="secondary" onClick={closeInvite}>{t('common.cancel')}</Button>
              <Button type="submit">{t('common.create')}</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={importOpen} title="Gebruikers importeren" onClose={closeImport}>
        {importResult ? (
          <div>
            <div class="alert alert-success mb-4">
              <strong>Import voltooid</strong>
              <div style="display:flex;flex-wrap:wrap;gap:16px;font-size:13px;margin-top:8px">
                <span>Uitgenodigd: <strong>{importResult.summary.invited || 0}</strong></span>
                <span>Heractiveerd: <strong>{importResult.summary.reactivated || 0}</strong></span>
                <span>Overgeslagen: <strong>{importResult.summary.skipped || 0}</strong></span>
                {importResult.summary.error > 0 && <span>Fouten: <strong>{importResult.summary.error}</strong></span>}
              </div>
            </div>
            {importResult.results.filter(r => r.status !== 'invited').length > 0 && (
              <div style="max-height:240px;overflow-y:auto;font-size:12px;border:1px solid var(--color-border);border-radius:6px">
                {importResult.results.filter(r => r.status !== 'invited').map((r, i) => (
                  <div key={i} style="padding:8px 12px;border-bottom:1px solid var(--color-border);display:flex;gap:8px;align-items:center">
                    <span style={`color:${r.status === 'error' ? 'var(--color-danger)' : 'var(--color-text-muted)'};flex-shrink:0`}>
                      {r.status === 'error' ? '✗' : r.status === 'reactivated' ? '↺' : '–'}
                    </span>
                    <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{r.email}</span>
                    <span style="color:var(--color-text-muted);flex-shrink:0">{r.reason || r.status}</span>
                  </div>
                ))}
              </div>
            )}
            <div class="modal-footer">
              <Button onClick={closeImport}>Sluiten</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleImportSubmit}>
            {importError && <div class="alert alert-error mb-4">{importError}</div>}

            <div style="border:1px solid var(--color-border);border-radius:8px;padding:16px;margin-bottom:20px">
              <p style="font-size:14px;font-weight:600;margin:0 0 6px">Stap 1 — Download het sjabloon</p>
              <p style="font-size:13px;color:var(--color-text-muted);margin:0 0 12px">Vul het Excel-bestand in met e-mailadres, naam en optioneel een rol per rij.</p>
              <button
                type="button"
                onClick={downloadTemplate}
                style="display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;color:var(--color-primary);background:transparent;padding:8px 14px;border:1px solid var(--color-primary);border-radius:6px;cursor:pointer"
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"/></svg>
                Sjabloon downloaden
              </button>
            </div>

            <div style="margin-bottom:0">
              <p style="font-size:14px;font-weight:600;margin:0 0 6px">Stap 2 — Upload het ingevulde bestand</p>
              <label style="display:flex;align-items:center;gap:10px;cursor:pointer">
                <span style="flex-shrink:0;padding:8px 14px;background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:6px;font-size:13px;font-weight:600;color:var(--color-text)">
                  Bestand kiezen
                </span>
                <span style="font-size:13px;color:var(--color-text-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                  {importFile ? importFile.name : 'Geen bestand geselecteerd'}
                </span>
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  required
                  style="display:none"
                  onChange={e => setImportFile(e.target.files[0])}
                />
              </label>
            </div>

            <div class="modal-footer">
              <Button type="button" variant="secondary" onClick={closeImport}>Annuleren</Button>
              <Button type="submit" disabled={importLoading || !importFile}>
                {importLoading ? 'Bezig…' : 'Importeren'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {upgradeModal && (
        <SubscriptionUpgradeModal
          isOpen={!!upgradeModal}
          currentTierLabel={upgradeModal.currentTierLabel}
          nextTier={upgradeModal.nextTier}
          onClose={() => setUpgradeModal(null)}
        />
      )}
    </div>
  );
}
